import { Box, Tooltip, Typography } from "@mui/joy";
import moment from "moment-timezone";
import { useState } from "react";
import { typeOptions } from "../../constants";
import { Contractor } from "../../entities/contractor";
import { Gig } from "../../entities/gig";
import { Project } from "../../entities/project";
import { Stage } from "../../entities/stage";
import { Venue } from "../../entities/venue";
import { AppConfig } from "../../environement";

const appleRed = "#FF3B30";

/**
 * Google Static Map URL centered on a venue (with a marker), used as the tile
 * hero when a service has no project cover. Returns undefined when the venue
 * has no coordinates or no maps key is configured.
 *
 * @param venue the resolved venue
 * @returns {string | undefined} the static map URL
 */
function venueMapURL(venue?: Venue): string | undefined {
  const key = (AppConfig.googleMaps as { geocoding?: string } | undefined)
    ?.geocoding;
  if (!key || !venue) return undefined;
  // Prefer coordinates; otherwise let the Static Maps API geocode the address.
  let at: string;
  if (venue.latitude && venue.longitude) {
    at = `${venue.latitude},${venue.longitude}`;
  } else {
    const q = [venue.name, venue.address, venue.city, venue.state]
      .filter((s) => s && `${s}`.trim())
      .join(", ");
    if (!q) return undefined;
    at = encodeURIComponent(q);
  }
  return (
    `https://maps.googleapis.com/maps/api/staticmap?center=${at}` +
    `&zoom=14&size=400x400&scale=2&maptype=roadmap` +
    `&markers=color:0xFF3B30%7C${at}` +
    `&style=feature:poi%7Celement:labels%7Cvisibility:off` +
    `&key=${key}`
  );
}

/**
 * Relative countdown wording for an upcoming service.
 *
 * @param gig the service
 * @param tz venue timezone
 * @returns {string | undefined} the label
 */
function relativeLabel(gig: Gig, tz?: string): string | undefined {
  if (gig.toBeDetermined() || gig.completed(tz)) return undefined;
  // Bucket both the service date and "now" in the same (venue) zone so the
  // day delta can't drift by one near midnight for cross-timezone viewers.
  const days = gig
    .from(tz)
    .clone()
    .startOf("day")
    .diff(moment.tz(gig.resolvedTz(tz)).startOf("day"), "days");
  if (days <= 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `In ${days} days`;
}

/**
 * Cover-dominant tile for a single service — an Apple Music / Apple TV style
 * card with the project artwork as a hero banner, the date/organization/
 * countdown floating on top, and the service details on a clean footer.
 * Used on the Journal/Home; lists keep the compact ServiceCard row.
 *
 * @param root0 props
 * @param root0.gig the service
 * @param root0.venue resolved venue
 * @param root0.contractor resolved organization
 * @param root0.project resolved project (its coverURL is the hero)
 * @param root0.stage resolved stage
 * @param root0.index position (for the staggered entrance animation)
 * @param root0.onClick tap handler
 * @returns {ReactElement} ServiceTile
 */
export function ServiceTile({
  gig,
  venue,
  contractor,
  project,
  stage,
  index = 0,
  onClick,
}: {
  gig: Gig;
  venue?: Venue;
  contractor?: Contractor;
  project?: Project;
  stage?: Stage;
  index?: number;
  onClick: (gig: Gig) => void;
}) {
  const tz = venue?.tzName;
  const tbd = gig.toBeDetermined();
  const today = gig.happeningToday(tz);
  const countdown = relativeLabel(gig, tz);
  const locked = gig.sourceID !== 420;
  const playing = gig.isPlaying();
  const stageColor = stage?.color || "#8E8E93";
  const showStage = stage && gig.stageID > 0 && playing;
  const sessionType = gig.sessionType();
  const sessionTypeIcon =
    typeOptions.find((t) => t.sessionTypeID === gig.sessionTypeID)?.icon ??
    "fa-solid fa-music";
  const cover = project?.coverURL;
  const heroColor = contractor?.color || "#0A84FF";
  const [mapBroken, setMapBroken] = useState(false);
  const mapUrl = venueMapURL(venue);
  // No project cover → show a map of the venue; gradient is the last resort.
  const showMap = !cover && !!mapUrl && !mapBroken;

  const frosted = {
    background: "rgba(0, 0, 0, 0.42)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
  } as const;

  return (
    <Box
      onClick={() => onClick(gig)}
      sx={{
        width: "100%",
        borderRadius: "20px",
        overflow: "hidden",
        background: "var(--mui-palette-background-paper, #fff)",
        border: "1px solid var(--mui-palette-divider)",
        boxShadow:
          "0 1px 2px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.06)",
        cursor: "pointer",
        transition: "all 0.25s cubic-bezier(0.3, 0, 0.2, 1)",
        animation: "cardIn 0.3s ease both",
        animationDelay: `${Math.min(index, 12) * 0.04}s`,
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow:
            "0 4px 8px rgba(0, 0, 0, 0.06), 0 16px 40px rgba(0, 0, 0, 0.12)",
        },
        "&:active": { transform: "scale(0.99)" },
      }}
    >
      {/* Hero cover (or a branded gradient fallback). Square only for real
          project artwork; without a cover a short banner carries the
          date/org/countdown chips over a map or gradient — a full square would
          just waste vertical space. Matches the mobile ServiceTile. */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: cover ? "1 / 1" : "16 / 7",
          overflow: "hidden",
          background: cover
            ? "#000"
            : showMap
            ? "#e5e7eb"
            : `linear-gradient(135deg, ${heroColor} 0%, #081D3A 130%)`,
        }}
      >
        {cover ? (
          <Box
            component="img"
            src={cover}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : showMap ? (
          <Box
            component="img"
            src={mapUrl}
            onError={() => setMapBroken(true)}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(255, 255, 255, 0.22)",
              fontSize: 72,
            }}
          >
            <i className={sessionTypeIcon}></i>
          </Box>
        )}
        {/* Scrim so the floating chips stay legible on any artwork */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.34) 0%, rgba(0,0,0,0) 28%, rgba(0,0,0,0) 64%, rgba(0,0,0,0.40) 100%)",
          }}
        />

        {/* Date — top-left, with month so the period is always clear */}
        <Box
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            minWidth: 46,
            borderRadius: "12px",
            px: 1,
            py: 0.5,
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            ...frosted,
          }}
        >
          {tbd ? (
            <Typography sx={{ fontSize: 18, color: "#fff", py: 0.5 }}>
              <i className="fa-solid fa-question"></i>
            </Typography>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  lineHeight: 1.2,
                  color: "#fff",
                }}
              >
                {gig.from(tz).format("ddd").toUpperCase()}
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.375rem",
                  fontWeight: 700,
                  lineHeight: 1.05,
                  color: "#fff",
                }}
              >
                {gig.from(tz).format("D")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.5625rem",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  lineHeight: 1.2,
                  color: "rgba(255,255,255,0.8)",
                }}
              >
                {gig.from(tz).format("MMM").toUpperCase()}{" "}
                {gig.from(tz).format("YYYY")}
              </Typography>
            </>
          )}
        </Box>

        {/* Organization — top-right */}
        {contractor?.coverURL ? (
          <Tooltip size="sm" arrow title={contractor?.name}>
            <Box
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                background: contractor.color || "#fff",
                borderRadius: "8px",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.25)",
              }}
            >
              <img
                src={contractor.coverURL}
                height={26}
                style={{
                  maxWidth: 120,
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          </Tooltip>
        ) : contractor?.name ? (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              px: 1,
              py: 0.5,
              borderRadius: "999px",
              maxWidth: "55%",
              ...frosted,
            }}
          >
            <Typography
              sx={{ color: "#fff", fontWeight: 600, fontSize: "0.75rem" }}
              noWrap
            >
              {contractor.name}
            </Typography>
          </Box>
        ) : (
          []
        )}

        {/* Countdown — bottom-right */}
        {countdown ? (
          <Box
            sx={{
              position: "absolute",
              bottom: 12,
              right: 12,
              px: 1.25,
              py: 0.375,
              borderRadius: "999px",
              ...(today ? { background: "rgba(255, 59, 48, 0.92)" } : frosted),
            }}
          >
            <Typography
              sx={{ color: "#fff", fontWeight: 700, fontSize: "0.75rem" }}
            >
              {countdown}
            </Typography>
          </Box>
        ) : (
          []
        )}
      </Box>

      {/* Footer — service details */}
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
        >
          {locked ? (
            <Typography
              sx={{
                fontSize: 11,
                flexShrink: 0,
                color: "var(--mui-palette-text-tertiary)",
              }}
            >
              <i className="fa-solid fa-lock"></i>
            </Typography>
          ) : (
            []
          )}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.0625rem",
              letterSpacing: "-0.01em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {gig.title ?? "Untitled Service"}
          </Typography>
          <Typography
            sx={{
              flexShrink: 0,
              fontSize: "0.6875rem",
              fontWeight: 600,
              whiteSpace: "nowrap",
              // Playing → plain green text; Not playing → solid red pill.
              ...(playing
                ? {
                    color: "#1B7A33",
                    '[data-joy-color-scheme="dark"] &, [data-mui-color-scheme="dark"] &':
                      { color: "#5DE27E" },
                  }
                : {
                    color: "#fff",
                    background: "#C9251C",
                    px: 0.75,
                    py: 0.125,
                    borderRadius: "999px",
                  }),
            }}
          >
            {playing ? "Playing" : "Not playing"}
          </Typography>
        </Box>
        {project ? (
          <Typography
            level="body3"
            sx={{ color: "var(--mui-palette-text-secondary)" }}
            noWrap
            startDecorator={
              <i className="fa-regular fa-folder" style={{ fontSize: 11 }}></i>
            }
          >
            {project.name}
          </Typography>
        ) : (
          []
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            columnGap: 1.5,
            rowGap: 0.25,
            flexWrap: "wrap",
            mt: 0.25,
          }}
        >
          {sessionType ? (
            <Typography
              level="body3"
              sx={{
                color: "var(--mui-palette-text-secondary)",
                fontWeight: 600,
              }}
              startDecorator={
                <i className={sessionTypeIcon} style={{ fontSize: 11 }}></i>
              }
            >
              {sessionType}
            </Typography>
          ) : (
            []
          )}
          {!tbd && !gig.noTime(tz) ? (
            <Typography
              level="body3"
              sx={{ color: "var(--mui-palette-text-secondary)" }}
              startDecorator={
                <i className="fa-regular fa-clock" style={{ fontSize: 11 }}></i>
              }
            >
              {gig.startTime(tz)} – {gig.endTime(tz)} {gig.from(tz).format("z")}
            </Typography>
          ) : (
            []
          )}
        </Box>
        <Typography
          level="body3"
          sx={{
            color: "var(--mui-palette-text-secondary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          startDecorator={
            <i
              className="fa-solid fa-location-dot"
              style={{ fontSize: 11 }}
            ></i>
          }
        >
          {gig.venueID === -1
            ? "Venue to be determined"
            : venue
            ? `${venue.name} · ${venue.city}, ${venue.state}`
            : "Unknown venue"}
        </Typography>

        {/* Status — stage only (Playing/Not playing now sits by the title) */}
        {showStage ? (
          <Box sx={{ mt: 0.75 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: "999px",
                background: `${stageColor}1A`,
              }}
            >
              {gig.isCattleCall() ? (
                <i
                  className="fa-solid fa-cowbell fa-shake"
                  style={{ fontSize: 12, color: stageColor }}
                />
              ) : (
                <img src={`${stage?.icon}`} style={{ height: 12 }} />
              )}
              <Typography
                sx={{
                  color: stageColor,
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              >
                {stage?.wording()}
              </Typography>
            </Box>
          </Box>
        ) : (
          []
        )}
      </Box>
    </Box>
  );
}
